/**
 * The **move-selection seam** — the pluggable policy-wrapper layer `autoplayActivation`
 * (`./autoplay.ts`) consults instead of hardcoding "take the top-ranked plan." The plain
 * selector — always `ranked[0]`, the argmax over `rankMacroMoves`'s already-scored top-k — is
 * explicit and injectable here as {@link argmaxMoveSelector}, and is `autoplayActivation`'s
 * **default**.
 *
 * {@link epsilonGreedySelector} is the MECHANISM behind the CLI's `--policy-noise <eps>`: with
 * probability `eps`, pick uniformly among the top-k plans OTHER
 * than the argmax, using a seeded policy-RNG; otherwise fall back to the argmax exactly like
 * `argmaxMoveSelector`. Seeded from a policy sub-seed that's salt-separated off the SAME per-run
 * seed the dice axis uses (`./seeding.ts`'s `policySeedFor`), so a sweep stays reproducible from
 * one master seed while the two axes (dice vs. policy) never correlate.
 *
 * **The context is wider than the two shipped selectors need.** A search-backed selector
 * (`./search.ts`'s `makeSearchMoveSelector`) needs more than the pre-ranked `ranked` list to do
 * its own move-generation (it has to score the OPPONENT's replies too, which `ranked` never
 * carries), so {@link MoveSelectorContext} also carries the raw `state`/`actorId`/`compendium`/
 * `overlay`/`terms`/`maxDestinations` `autoplayActivation` already holds locally. All are
 * OPTIONAL, so {@link argmaxMoveSelector}/{@link epsilonGreedySelector} (and any call site
 * passing a bare `{ ranked }`) read only `ranked`.
 */
import type { EncounterState } from "@lancer-vtt/domain";
import type { Compendium } from "@lancer-vtt/compendium";
import type { LoadedOverlay } from "@lancer-vtt/rules";
import type { RankedMacroMove } from "./advisor";
import type { FeatureTerm } from "./evaluator";
import { splitmix32Stream } from "./seeding";

/**
 * The context a {@link MoveSelector} receives. `ranked` (`rankMacroMoves`'s top-k, already
 * scored descending) is the only field the shipped argmax/epsilon-greedy selectors read; the
 * rest are optional context a search-backed selector needs (see file header).
 */
export type MoveSelectorContext = {
  ranked: RankedMacroMove[];
  state?: EncounterState;
  actorId?: string;
  compendium?: Compendium;
  overlay?: LoadedOverlay;
  terms?: readonly FeatureTerm[];
  maxDestinations?: number;
};

/**
 * Choose one plan from `ctx.ranked` — or `undefined` when there's nothing to choose (an empty
 * `ranked`, mirroring `ranked[0]`'s own `undefined` when the array is empty). `autoplayActivation`
 * treats `undefined` exactly like today's "no legal macro-move" case.
 */
export type MoveSelector = (ctx: MoveSelectorContext) => RankedMacroMove | undefined;

/** The default selector — always the argmax (`ranked[0]`). This is `autoplayActivation`'s pre-Phase-2.2 implicit behavior, made explicit and injectable but left byte-identical. */
export const argmaxMoveSelector: MoveSelector = ({ ranked }) => ranked[0];

export type EpsilonGreedySelectorArgs = {
  /** Probability, per selection, of picking a non-argmax plan from the top-k instead of the argmax. `eps <= 0` degenerates to exactly {@link argmaxMoveSelector}'s behavior (see below — the "no policy noise" invariant `combat-difficulty`'s tracker pins). */
  eps: number;
  /** The salt-separated policy sub-seed (`./seeding.ts`'s `policySeedFor`) — a DEDICATED seeded stream, independent of the dice `DiceSource` the SAME per-run seed also feeds. */
  policySeed: number;
};

/**
 * Build an epsilon-greedy {@link MoveSelector}: with probability `eps`, uniformly pick one of
 * `ranked`'s entries OTHER than index 0; otherwise (probability `1 - eps`, and always when
 * `eps <= 0` or `ranked` has no non-argmax alternative) return the argmax, exactly like
 * {@link argmaxMoveSelector}. Every call draws from its own seeded stream (`splitmix32Stream`,
 * `./seeding.ts` — the SAME generator `perRunSeeds` uses, kept distinct from the dice LCG), so
 * repeated calls against the SAME `policySeed` reproduce the SAME sequence of choices.
 *
 * **The load-bearing invariant this selector must never violate:** at `eps <= 0` it degenerates
 * to `ranked[0]` on every call — no RNG draw changes that outcome, no branch differs from
 * `argmaxMoveSelector`. A sweep run with `policyNoise: { eps: 0 }` must therefore be
 * byte-identical to one run with no `policyNoise` at all (pinned by `sweep.test.ts`).
 */
export function epsilonGreedySelector(args: EpsilonGreedySelectorArgs): MoveSelector {
  const { eps, policySeed } = args;
  const next = splitmix32Stream(policySeed);
  return ({ ranked }) => {
    if (ranked.length === 0) return undefined;
    // `eps <= 0` (or no non-argmax alternative to pick) — degenerate to the argmax with NO RNG
    // draw at all, so the eps=0 path never diverges from `argmaxMoveSelector` even by an unused
    // draw's side effect on a shared stream (there is none here, but the early-return keeps the
    // invariant obviously true by inspection, not by coincidence).
    if (eps <= 0 || ranked.length === 1) return ranked[0];
    const roll = next() / 0x100000000;
    if (roll >= eps) return ranked[0];
    const idx = 1 + Math.floor((next() / 0x100000000) * (ranked.length - 1));
    return ranked[idx] ?? ranked[0];
  };
}
